import { WitboostCommercialModule } from '@agilelab/plugin-wb-platform-common';
import { z } from 'zod';

const WebhookHeaderSchema = z.object({
  webhook_uuid: z.string(),
  key: z.string(),
  value: z.string(),
  sensitive: z.boolean(),
});
export type WebhookHeader = z.infer<typeof WebhookHeaderSchema>;

export const HeadersByWebhookGetRequestSchema = z.object({
  webhook_uuid: z.string(),
  redactWith: z.string().optional(),
});

export type HeadersByWebhookGetRequest = z.infer<
  typeof HeadersByWebhookGetRequestSchema
>;

export type HeadersByWebhookGetResponse = {
  data: WebhookHeader[];
};

export const WebhookHeaderUpsertRequestSchema = WebhookHeaderSchema;

export type WebhookHeaderUpsertRequest = z.infer<
  typeof WebhookHeaderUpsertRequestSchema
>;

export type WebhookHeaderUpsertResponse = WebhookHeader;

export const WebhookHeaderDeleteRequestSchema = WebhookHeaderSchema.pick({
  webhook_uuid: true,
  key: true,
});

export type WebhookHeaderDeleteRequest = z.infer<
  typeof WebhookHeaderDeleteRequestSchema
>;

export type WebhookHeaderDeleteResponse = boolean;

const WebhookHeadersSchema = z.record(z.string());

export type WebhookHeaders = z.infer<typeof WebhookHeadersSchema>;

export type NotificationKindMetadata = {
  kind: string;
  description: string;
  affectedModules: WitboostCommercialModule[];
  supportedRecipientTypes: string[];
  triggerTopicIds: string[];
  bodySchema?: Record<string, unknown>;
  additionalDocs?: { title: string; content: string }[];
};

export type NotificationKindsGetResponse = {
  kinds: NotificationKindMetadata[];
};

export const WebhookSchema = z.object({
  url: z.string().url(),
  uuid: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Webhook = z.infer<typeof WebhookSchema>;

export const NotificationBindingSchema = z.object({
  id: z.number(),
  webhook_uuid: z.string(),
  notification_kind: z.string(),
  enabled: z.boolean(),
});

export type NotificationBinding = z.infer<typeof NotificationBindingSchema>;

export const WritableNotificationBindingMetadataSchema =
  NotificationBindingSchema.omit({
    id: true,
  });

export type WritableNotificationBindingMetadata = z.infer<
  typeof WritableNotificationBindingMetadataSchema
>;

export const NotificationBindingRecipientTypeSchema = z.object({
  type: z.string(),
  enabled: z.boolean(),
});

export type NotificationBindingRecipientType = z.infer<
  typeof NotificationBindingRecipientTypeSchema
>;

export const NotificationBindingWithRecipientTypesSchema =
  NotificationBindingSchema.extend({
    recipient_types: z.array(NotificationBindingRecipientTypeSchema).optional(),
  });

export type NotificationBindingWithRecipientTypes = z.infer<
  typeof NotificationBindingWithRecipientTypesSchema
>;

export const WritableWebhookMetadataSchema = WebhookSchema.omit({
  uuid: true,
  created_at: true,
  updated_at: true,
});

export type WritableWebhookMetadata = z.infer<
  typeof WritableWebhookMetadataSchema
>;

export const WebhooksGetRequestSchema = z.object({
  search: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export const WebhookGetRequestSchema = z.object({
  uuid: z.string(),
});
export type WebhookGetRequest = z.infer<typeof WebhookGetRequestSchema>;

export type WebhookGetResponse = Webhook | undefined;

export type WebhooksGetRequest = z.infer<typeof WebhooksGetRequestSchema>;

export type WebhooksGetResponse = {
  data: Webhook[];
  meta: {
    pagination: {
      total: number;
      limit?: number;
      offset?: number;
    };
  };
};

export const WebhookPatchRequestSchema = z.object({
  uuid: z.string(),
  body: WritableWebhookMetadataSchema.partial(),
});
export type WebhookPatchRequest = z.infer<typeof WebhookPatchRequestSchema>;

export const WebhookDeleteRequestSchema = z.object({
  uuids: z.array(z.string()),
});
export type WebhookDeleteRequest = z.infer<typeof WebhookDeleteRequestSchema>;

export const WebhookCreateRequestSchema = WritableWebhookMetadataSchema;
export type WebhookCreateRequest = z.infer<typeof WebhookCreateRequestSchema>;

export const NotificationBindingInsertRequestSchema =
  WritableNotificationBindingMetadataSchema;

export type NotificationBindingInsertRequest = z.infer<
  typeof NotificationBindingInsertRequestSchema
>;

export const NotificationBindingPatchRequestSchema = z.object({
  id: z.number(),
  body: WritableNotificationBindingMetadataSchema.partial(),
});

export type NotificationBindingPatchRequest = z.infer<
  typeof NotificationBindingPatchRequestSchema
>;

export const NotificationBindingDeleteRequestSchema = z.object({
  ids: z.array(z.number()),
});
export type NotificationBindingDeleteRequest = z.infer<
  typeof NotificationBindingDeleteRequestSchema
>;

export const NotificationBindingsGetRequestSchema = z.object({
  webhook_uuid: z.string().optional(),
  notification_kind: z.string().optional(),
  enabled: z.boolean().optional(),
  includeRecipientTypes: z.boolean().optional(),
});
export type NotificationBindingsGetRequest = z.infer<
  typeof NotificationBindingsGetRequestSchema
>;

export type NotificationBindingsGetResponse = {
  data: NotificationBindingWithRecipientTypes[];
};
